import React, { useState, useEffect, useRef } from 'react';
import { AddClientOffcanvas } from './add';
import { DeleteClientModal } from './delete';
import { EditClientOffcanvas } from './edit';
import { ClientsList } from './list';
import type { Filters as ClientFilters } from './list';

import api from '../../libs/axios';
import { PaginationType } from '../../utils/pagination.utils';
import { useDebounce } from '../../hooks/useDebounce';

interface ClientRepresentative {
    id: number;
    contact_person: string;
    contact_number: string;
    designation: string;
    representative_location: string;
    client_id: number;
    created_at: string;
    updated_at: string;
}

interface Client {
    id: number;
    client_full_name: string;
    client_short_name: string;
    address: string;
    gst_number: string;
    state: string;
    is_active: boolean;
    created_by: number;
    suspended_by?: number | null;
    suspended_at?: string | null;
    created_at: string;
    updated_at: string;
    representatives?: ClientRepresentative[];
    client_projects?: any[];
}

interface ClientFormData {
    client_full_name: string;
    client_short_name: string;
    address: string;
    gst_number: string;
    state: string;
    representatives?: {
        contact_person: string;
        contact_number: string;
        designation: string;
        representative_location: string;
    }[];
}

interface EditClientFormData extends ClientFormData {
    id: number;
    is_active?: boolean;
}

export const ClientManagement: React.FC = () => {
    // State management
    const [clients, setClients] = useState<Client[]>([]);
    const [pagination, setPagination] = useState<PaginationType>({
        offset: 0,
        limit: 10,
        page: 1,
        total_pages: 0,
        total_count: 0,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null
    });

    // Loading states
    const [clientsLoading, setClientsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Modal/Offcanvas states
    const [showAddClient, setShowAddClient] = useState(false);
    const [showEditClient, setShowEditClient] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [advancedFilters, setAdvancedFilters] = useState<ClientFilters>({});
    const advancedFiltersRef = useRef<ClientFilters>({});
    const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
        field: 'created_at',
        direction: 'DESC'
    });

    // Debounced search — API fires only after 400 ms of inactivity
    const debouncedSearch = useDebounce(searchTerm, 400);

    // API Functions
    const fetchClients = async (
        search: string = '',
        page: number = 1,
        status?: string,
        limit?: number,
        filtersOverride?: ClientFilters,
        sort = sortConfig
    ) => {
        const filters = filtersOverride ?? advancedFiltersRef.current;
        setClientsLoading(true);
        try {
            const effectiveLimit = limit || pagination.limit;
            const queryParams: any = {
                page,
                limit: effectiveLimit
            };

            const effectiveSearch = search.trim() || (filters.search?.trim() ?? '');
            if (effectiveSearch) {
                queryParams.search = effectiveSearch;
            }

            const rawStatus = filters.status || status;
            if (rawStatus && rawStatus !== 'all') {
                queryParams.status = rawStatus.toLowerCase();
            }

            if (filters.name?.trim()) {
                queryParams.name = filters.name.trim();
            }

            if (filters.gst_number?.trim()) {
                queryParams.gst_number = filters.gst_number.trim();
            }

            if (filters.address?.trim()) {
                queryParams.address = filters.address.trim();
            }

            if (filters.state_id?.trim()) {
                queryParams.state_id = filters.state_id.trim();
            }

            if (filters.created_from) {
                queryParams.created_from = filters.created_from;
            }

            if (filters.created_to) {
                queryParams.created_to = filters.created_to;
            }

            if (filters.has_representatives) {
                queryParams.has_representatives = filters.has_representatives;
            }

            if (filters.has_projects) {
                queryParams.has_projects = filters.has_projects;
            }

            if (filters.sortBy) {
                queryParams.sort_by = filters.sortBy;
            }

            if (filters.sortOrder) {
                queryParams.sort_order = filters.sortOrder;
            }

            if (sort?.field) {
                queryParams.sort_by = sort.field;
                queryParams.sort_order = sort.direction;
            }

            const response = await api.get('/clients/list', {
                params: queryParams
            });

            if (response.data.success) {
                setClients(response.data.data.clients || []);
                setPagination(response.data.data.pagination || pagination);
            } else {
                console.error('API returned success: false');
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
        } finally {
            setClientsLoading(false);
        }
    };

    const fetchClientById = async (clientId: number): Promise<Client | null> => {
        try {
            const response = await api.get(`/clients/${clientId}`);

            if (response.data.success) {
                return response.data.data.client;
            }
            return null;
        } catch (error) {
            console.error('Error fetching client by ID:', error);
            return null;
        }
    };

    const createClient = async (clientData: ClientFormData) => {
        setActionLoading(true);
        try {
            const response = await api.post('/clients/create', clientData);

            if (response.data.success) {
                await fetchClients();
                setShowAddClient(false);
                console.log('Client created successfully');
            } else {
                console.error('Failed to create client:', response.data.message);
            }
        } catch (error) {
            console.error('Error creating client:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const updateClient = async (clientData: EditClientFormData) => {
        setActionLoading(true);
        try {
            const response = await api.put(`/clients/${clientData.id}`, clientData);

            if (response.data.success) {
                await fetchClients();
                setShowEditClient(false);
                setSelectedClient(null);
                console.log('Client updated successfully');
            } else {
                console.error('Failed to update client:', response.data.message);
            }
        } catch (error) {
            console.error('Error updating client:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const deleteClient = async () => {
        if (!selectedClient) return;

        setActionLoading(true);
        try {
            const response = await api.delete(`/clients/${selectedClient.id}`);

            if (response.data.success) {
                await fetchClients();
                setShowDeleteModal(false);
                setSelectedClient(null);
                console.log('Client deleted successfully');
            } else {
                console.error('Failed to delete client:', response.data.message);
            }
        } catch (error) {
            console.error('Error deleting client:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const toggleClientStatus = async (client: Client) => {
        setActionLoading(true);
        try {
            const response = await api.patch(`/clients/${client.id}/status`, {
                status: client.is_active ? 'SUSPENDED' : 'ACTIVE'
            });

            if (response.data.success) {
                await fetchClients();
                console.log(`Client ${!client.is_active ? 'activated' : 'suspended'} successfully`);
            } else {
                console.error('Failed to toggle client status:', response.data.message);
            }
        } catch (error) {
            console.error('Error toggling client status:', error);
        } finally {
            setActionLoading(false);
        }
    };

    // Event handlers
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        // Actual API call is triggered by the debouncedSearch effect below
    };

    // Fire fetch when debounced search value settles
    useEffect(() => {
        fetchClients(debouncedSearch, 1, statusFilter, undefined, undefined, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const handleFilterChange = (filter: string) => {
        setStatusFilter(filter);
        fetchClients(searchTerm, 1, filter, undefined, undefined, sortConfig);
    };

    const handlePageChange = (page: number) => {
        fetchClients(searchTerm, page, statusFilter, undefined, undefined, sortConfig);
    };

    const handleLimitChange = (limit: number) => {
        fetchClients(searchTerm, 1, statusFilter, limit, undefined, sortConfig);
    };

    const handleAdvancedFilter = (filters: ClientFilters) => {
        advancedFiltersRef.current = filters;
        setAdvancedFilters(filters);

        if (filters.search !== undefined) {
            setSearchTerm(filters.search || '');
        }

        const effectiveSearch = filters.search ?? searchTerm;
        const effectiveStatus = filters.status ?? statusFilter;
        fetchClients(effectiveSearch, 1, effectiveStatus, undefined, filters, sortConfig);
    };

    const handleAddClient = () => {
        setShowAddClient(true);
    };

    const handleEditClient = async (client: Client) => {
        // Fetch full client details if needed
        const fullClient = await fetchClientById(client.id);
        if (fullClient) {
            setSelectedClient(fullClient);
            setShowEditClient(true);
        }
    };

    const handleDeleteClient = (client: Client) => {
        setSelectedClient(client);
        setShowDeleteModal(true);
    };

    const handleCloseModals = () => {
        setShowAddClient(false);
        setShowEditClient(false);
        setShowDeleteModal(false);
        setSelectedClient(null);
    };

    const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
        const sort = { field, direction };
        setSortConfig(sort);
        fetchClients(searchTerm, pagination.page, statusFilter, undefined, undefined, sort);
    };

    // Initialize data on component mount
    useEffect(() => {
        fetchClients();
    }, []);

    return (
        <div className="">
            <div className="">
                {/* Main Clients List */}
                <ClientsList
                    clients={clients}
                    pagination={pagination}
                    onSearch={handleSearch}
                    onFilterChange={handleFilterChange}
                    statusFilter={statusFilter}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    onAddClient={handleAddClient}
                    onEditClient={handleEditClient}
                    onDeleteClient={handleDeleteClient}
                    onToggleStatus={toggleClientStatus}
                    onAdvancedFilter={handleAdvancedFilter}
                    onSort={handleSort}
                    loading={clientsLoading}
                    actionLoading={actionLoading}
                    searchValue={searchTerm}
                    statusValue={statusFilter}
                    appliedFilters={advancedFilters}
                    sortState={sortConfig}
                />

                {/* Add Client Offcanvas */}
                <AddClientOffcanvas
                    isOpen={showAddClient}
                    onClose={handleCloseModals}
                    onSubmit={createClient}
                    loading={actionLoading}
                />

                {/* Edit Client Offcanvas */}
                <EditClientOffcanvas
                    isOpen={showEditClient}
                    onClose={handleCloseModals}
                    onSubmit={updateClient}
                    client={selectedClient}
                    loading={actionLoading}
                />

                {/* Delete Client Modal */}
                <DeleteClientModal
                    isOpen={showDeleteModal}
                    onClose={handleCloseModals}
                    onDelete={deleteClient}
                    text={
                        selectedClient
                            ? `Are you sure you want to delete the client "${selectedClient.client_full_name}"? This action cannot be undone and will remove all associated data.`
                            : ''
                    }
                    loading={actionLoading}
                />
            </div>
        </div>
    );
};